import { ExternalLink, FolderOpen, Loader2, AlertTriangle } from 'lucide-react';
import { useAccountDriveFiles } from '@/features/accounts/hooks/useAccountDriveFiles';
import type { DriveFile } from '@/features/accounts/api';
import { formatBytes } from '@/lib/utils';

function formatDate(iso?: string): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}

function FileRow({ file }: { file: DriveFile }) {
  const isFolder = file.mimeType === 'application/vnd.google-apps.folder';
  const meta = [formatDate(file.modifiedTime), file.size ? formatBytes(Number(file.size)) : ''].filter(Boolean).join(' · ');
  return (
    <a
      href={file.webViewLink ?? '#'}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50"
    >
      {file.iconLink ? (
        <img src={file.iconLink} alt="" className="h-4 w-4 shrink-0" />
      ) : (
        <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
      )}
      <span className="text-sm flex-1 truncate">
        {file.name}
        {isFolder && <span className="text-xs text-muted-foreground"> (folder)</span>}
      </span>
      {meta && <span className="text-xs text-muted-foreground shrink-0">{meta}</span>}
      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" />
    </a>
  );
}

export function SupplierDriveFiles({ supplierId }: { supplierId: string }) {
  const { data, isLoading, isError, error } = useAccountDriveFiles(supplierId);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading Drive files…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-start gap-2 rounded-md border bg-destructive/5 p-2 text-xs text-destructive">
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>{error instanceof Error ? error.message : 'Could not load Drive files'}</span>
      </div>
    );
  }

  if (!data || data.status === 'not_configured') {
    return (
      <p className="text-xs text-muted-foreground italic">
        No Drive folder linked yet. Paste a Drive folder URL or ID above to see its contents.
      </p>
    );
  }

  if (data.status === 'forbidden') {
    return (
      <div className="rounded-md border bg-amber-50 dark:bg-amber-950/20 p-2 text-xs space-y-1">
        <div className="flex items-center gap-1.5 font-medium text-amber-900 dark:text-amber-200">
          <AlertTriangle className="h-3.5 w-3.5" /> Folder not accessible
        </div>
        {data.serviceAccountEmail && (
          <p className="text-amber-800 dark:text-amber-300">
            Share with{' '}
            <code className="font-mono">{data.serviceAccountEmail}</code> (Viewer).
          </p>
        )}
      </div>
    );
  }

  if (data.status === 'not_found') {
    return (
      <div className="rounded-md border bg-destructive/5 p-2 text-xs text-destructive">
        <div className="flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5" /> Folder ID does not resolve.
        </div>
      </div>
    );
  }

  if (data.files.length === 0) {
    return <p className="text-xs text-muted-foreground italic">This folder is empty.</p>;
  }

  return (
    <div className="space-y-0.5 rounded-md border bg-muted/20 p-1">
      {data.files.map((f) => (
        <FileRow key={f.id} file={f} />
      ))}
    </div>
  );
}
